import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, OnInit, ViewChild, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { debounceTime } from 'rxjs';

import { MatIconButton } from '@angular/material/button';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';

import { MemberProfile } from '@lib/core/models/member.interface';

import { AppointmentType, UploadingMedicalDocumentItem } from '@lib/medical-documents/core/models/document.interface';
import { DocumentDetailsState } from '@lib/medical-documents/core/models/medical-documents-state.interface';
import {
  addUploadingDocument,
  clearCurrentDocument,
  clearDocumentsList,
  contextSearchDocuments,
  setAiSearchChecked,
  setDocumentDetailsState,
  setDocumentsSearchTerm,
  uploadDocument,
} from '@lib/medical-documents/core/store/medical-documents.actions';
import {
  selectAiDocumentsList,
  selectCurrentAiDocument,
  selectDocumentDetailsState,
  selectDocumentsSearchTerm,
  selectIsAiMedicalDataEnabled,
  selectIsAiSearchChecked,
  selectIsAiSearchFeatureEnabled,
  selectUploadingDocuments,
} from '@lib/medical-documents/core/store/medical-documents.selectors';
import { MD_LIB_CONFIG } from '@lib/medical-documents/core/tokens/medical-documents.tokens';

import { UploadErrorDialogData, UploadErrorModalComponent } from '../modals/upload-error-dialog/upload-error-modal.component';

@Component({
  selector: 'md-search',
  standalone: true,
  imports: [ReactiveFormsModule, MatFormField, MatLabel, MatInput, MatIconButton, MatSuffix, MatIcon, MatTooltip, TranslatePipe, MatCheckbox],
  templateUrl: './document-search.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentSearchComponent implements OnInit {
  @ViewChild('fileInput') fileInput!: ElementRef;

  private readonly destroyRef = inject(DestroyRef);
  private readonly mdConfig = inject(MD_LIB_CONFIG);
  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);

  protected readonly isAiSearchFeatureEnabled = this.store.selectSignal<boolean>(selectIsAiSearchFeatureEnabled);
  protected readonly isAiMedicalDataEnabled = this.store.selectSignal<boolean>(selectIsAiMedicalDataEnabled);
  protected readonly isAiSearchChecked = this.store.selectSignal<boolean>(selectIsAiSearchChecked);
  protected readonly documentsSearchTerm = this.store.selectSignal<string>(selectDocumentsSearchTerm);
  protected readonly detailsState = this.store.selectSignal(selectDocumentDetailsState);
  protected readonly aiDocuments = this.store.selectSignal(selectAiDocumentsList);

  protected readonly memberProfile = signal<MemberProfile>(this.mdConfig.memberProfile);
  protected readonly isAiDocumentSelected = computed<boolean>(() => !!this.store.selectSignal(selectCurrentAiDocument)());
  protected readonly showDetailsButton = computed<boolean>(() => this.detailsState() === DocumentDetailsState.Closed && this.isAiDocumentSelected());
  protected readonly isUploadBtnVisible = computed<boolean>(() => this.isAiMedicalDataEnabled() && (this.mdConfig.documentsUploadEnabled ?? true));
  protected readonly isUploadBtnDisabled = computed<boolean>(() => !!this.store.selectSignal(selectUploadingDocuments)().length);
  protected readonly isContextSearchVisible = computed<boolean>(() => this.isAiMedicalDataEnabled() && this.isAiSearchFeatureEnabled() && (this.mdConfig.aiContextSearchEnabled ?? false));
  protected readonly contextSearchFlagControl = new FormControl(false, { nonNullable: true });
  protected readonly searchControl = new FormControl(this.documentsSearchTerm(), { nonNullable: true });

  ngOnInit() {
    this.initSearchWatcher();
  }

  protected clearSearch(): void {
    this.searchControl.setValue('');
  }

  protected onAiToggleChange(event: MatCheckboxChange): void {
    this.contextSearchFlagControl.setValue(event.checked);
    this.store.dispatch(setAiSearchChecked({ checked: event.checked }));
    this.searchControl.setValue(this.documentsSearchTerm());
  }

  private initSearchWatcher() {
    this.searchControl.valueChanges.pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      const modifiedValue = value.trim().length > 2 ? value.trim() : '';
      this.store.dispatch(clearCurrentDocument());
      this.store.dispatch(setDocumentsSearchTerm({ documentsSearchTerm: modifiedValue }));

      if (this.isAiSearchFeatureEnabled() && this.isAiSearchChecked() && modifiedValue) {
        this.store.dispatch(clearDocumentsList());
        const userId = this.mdConfig.userId || this.memberProfile()?.userId;
        if (userId) {
          this.store.dispatch(contextSearchDocuments({ userId, searchTerm: modifiedValue }));
        }
      }
    });
  }

  protected openArrayBuffer() {
    this.fileInput.nativeElement.click();
  }

  protected onFileSelected($event: Event) {
    const input = $event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (file) {
      this.uploadMedicalDocument(file);
      input.value = '';
    }
  }

  protected uploadMedicalDocument(file: File) {
    const MAX_FILE_SIZE: number = 20 * 1024 * 1024; // 20MB in bytes
    const fileExtension: string = file.name.split('.').pop().toLowerCase();

    if (file.size > MAX_FILE_SIZE) {
      this.showUploadError({
        message: 'Помилка завантаження!',
        additionalText: 'Розмір файлу не повинен перевищувати 20 Мб.',
      });
      return;
    }

    if (fileExtension !== 'pdf' && fileExtension !== 'jpeg' && fileExtension !== 'jpg' && fileExtension !== 'png') {
      this.showUploadError({
        message: 'Помилка завантаження!',
        additionalText: 'Підтримувані типи файлів: .pdf, .jpeg, .jpg, .png',
      });
      return;
    }

    const uploadingDocument: UploadingMedicalDocumentItem = {
      file,
      type: AppointmentType.Other,
      documentName: file.name.split('.')[0],
    };

    this.store.dispatch(addUploadingDocument({ uploadingDocument }));
    this.store.dispatch(
      uploadDocument({
        uploadingDocument,
        userId: this.memberProfile().userId,
      })
    );
  }

  private showUploadError(data: UploadErrorDialogData) {
    const dialogRef = this.dialog.open(UploadErrorModalComponent, {
      data,
      width: '800px',
      disableClose: false,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'tryAnother') this.openArrayBuffer();
    });
  }

  protected openDetails() {
    this.store.dispatch(setDocumentDetailsState({ state: DocumentDetailsState.Opened }));
  }
}
